import type { QueryKey } from '@tanstack/react-query';
import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import {
  BROWSER_AGENT_ENFORCEMENT_NOT_SET,
  BROWSER_AGENT_SITES_FILTERABLE_FIELDS as FIELDS,
  type BrowserAgentSitesFilterOptionField,
  type BrowserAgentSitesViewSite,
} from '@kirin/api-client/contracts/shadow-ai';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { queryKeys } from '@/hooks/api/queryKeys';
import { fetchBrowserAgentSitesFilterOptions } from './api';
import {
  AgentBadge,
  ApprovalCell,
  EnforcementToggle,
  UsersCount,
  ViaCell,
} from './cells';
import { AGENT_TYPE_META, type Enforcement } from './mockData';

const SetOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
];

const RangeOperators = [{ value: FilterOperators.INCLUDES, label: 'Range' }];

/** URL keys for the table's column state (filters + sort). */
const URL_KEYS: Record<string, string> = {
  [FIELDS.SITE_HOST]: 'site',
  [FIELDS.AGENT_NAME]: 'agt',
  [FIELDS.REACHED_VIA]: 'via',
  [FIELDS.USERS]: 'usr',
  [FIELDS.VISITS_30D]: 'vis',
  [FIELDS.LAST_SEEN]: 'ls',
  [FIELDS.ENFORCEMENT]: 'enf',
  [FIELDS.APPROVAL]: 'apr',
};

interface ColumnHandlers {
  organizationId: string;
  /** Owner/manager writes; support (and anyone while roles load) is read-only. */
  controlsDisabled: boolean;
  onDecide: (site: BrowserAgentSitesViewSite, action: Enforcement) => void;
  onUndo: (site: BrowserAgentSitesViewSite, enforcement: Enforcement) => void;
  onUsersClick: (site: BrowserAgentSitesViewSite) => void;
}

/**
 * Browser Agent Sites columns on the shared DataTable (KIR-4704 review): every
 * column sorts or filters server-side; the agent-type tabs are gone, replaced
 * by the Agent and Via column filters. Widths are fixed so sorting or paging
 * never re-flows the table; Site takes the remaining space.
 */
export function createBrowserAgentSitesColumns({
  organizationId,
  controlsDisabled,
  onDecide,
  onUndo,
  onUsersClick,
}: ColumnHandlers): ColumnConfig<BrowserAgentSitesViewSite>[] {
  const dynamicOptions = (field: BrowserAgentSitesFilterOptionField) => ({
    type: FilterOptionTypes.DYNAMIC,
    getQueryKey: (params: object): QueryKey => [
      ...queryKeys.shadowAi.browserAgentSites(organizationId),
      'filterOptions',
      field,
      params,
    ],
    queryFn: (
      params: Parameters<typeof fetchBrowserAgentSitesFilterOptions>[2]
    ) => fetchBrowserAgentSitesFilterOptions(organizationId, field, params),
    pagination: { mode: 'offset' as const, limit: 20 },
    queryConfig: { staleTime: 2 * 60 * 1000 },
  });

  return [
    {
      id: FIELDS.SITE_HOST,
      header: 'Site',
      accessor: 'siteHost',
      fill: true,
      minWidth: 180,
      renderCell: ({ row }) => (
        <span className="block truncate font-semibold" title={row.siteHost}>
          {row.siteHost}
        </span>
      ),
      sortable: true,
      sortField: 'siteHost',
      sortKey: URL_KEYS[FIELDS.SITE_HOST],
      filterable: true,
      // TEXT renders a text-input filter; STRING would need an option list.
      dataType: ColumnDataTypes.TEXT,
      filterOperators: [
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
      ],
      statePersistence: { urlKey: URL_KEYS[FIELDS.SITE_HOST] },
    },
    {
      id: FIELDS.AGENT_NAME,
      header: 'Agent',
      accessor: 'agentName',
      width: 195,
      renderCell: ({ row }) => <AgentBadge name={row.agentName} />,
      sortable: true,
      sortField: 'agentName',
      sortKey: URL_KEYS[FIELDS.AGENT_NAME],
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: SetOperators,
      statePersistence: { urlKey: URL_KEYS[FIELDS.AGENT_NAME] },
      filterOptions: {
        ...dynamicOptions(FIELDS.AGENT_NAME),
        render: option => <AgentBadge name={option.value} />,
      },
    },
    {
      id: FIELDS.REACHED_VIA,
      header: 'Via',
      accessor: 'reachedVia',
      width: 185,
      renderCell: ({ row }) => <ViaCell site={row} />,
      sortable: true,
      sortField: 'reachedVia',
      sortKey: URL_KEYS[FIELDS.REACHED_VIA],
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: SetOperators,
      statePersistence: { urlKey: URL_KEYS[FIELDS.REACHED_VIA] },
      filterOptions: dynamicOptions(FIELDS.REACHED_VIA),
    },
    {
      id: FIELDS.USERS,
      header: 'Users',
      accessor: 'users',
      width: 110,
      renderCell: ({ row }) => <UsersCount site={row} onClick={onUsersClick} />,
      sortable: true,
      sortField: 'users',
      sortKey: URL_KEYS[FIELDS.USERS],
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: RangeOperators,
      statePersistence: { urlKey: URL_KEYS[FIELDS.USERS] },
      range: { min: 0, max: 100, step: 1 },
    },
    {
      id: FIELDS.VISITS_30D,
      header: 'Visits (30d)',
      accessor: 'visits30d',
      width: 145,
      renderCell: ({ row }) => (
        <span className="font-semibold">{row.visits30d}</span>
      ),
      sortable: true,
      sortField: 'visits30d',
      sortKey: URL_KEYS[FIELDS.VISITS_30D],
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: RangeOperators,
      statePersistence: { urlKey: URL_KEYS[FIELDS.VISITS_30D] },
      range: { min: 0, max: 1000, step: 1 },
    },
    {
      id: FIELDS.LAST_SEEN,
      header: 'Last Seen',
      accessor: 'lastSeen',
      timestamp: true,
      renderCell: ({ row }) => <RelativeTimeWithTooltip date={row.lastSeen} />,
      sortable: true,
      sortField: 'lastSeen',
      sortKey: URL_KEYS[FIELDS.LAST_SEEN],
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: { urlKey: URL_KEYS[FIELDS.LAST_SEEN] },
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
    },
    {
      id: FIELDS.ENFORCEMENT,
      header: 'Enforcement',
      accessor: 'enforcement',
      width: 196,
      clipOverflow: false,
      renderCell: ({ row }) => (
        <EnforcementToggle
          options={AGENT_TYPE_META[row.agentType].options}
          value={row.enforcement}
          disabled={controlsDisabled}
          onChange={next => onDecide(row, next)}
        />
      ),
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: SetOperators,
      statePersistence: { urlKey: URL_KEYS[FIELDS.ENFORCEMENT] },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [
          { value: 'approve', label: 'Approve' },
          { value: 'block', label: 'Block' },
          { value: 'monitor', label: 'Monitor' },
          { value: BROWSER_AGENT_ENFORCEMENT_NOT_SET, label: 'Not set' },
        ],
      },
    },
    {
      id: FIELDS.APPROVAL,
      header: 'Approval',
      accessor: row => (row.approval ? 'reviewed' : 'not_reviewed'),
      width: 180,
      clipOverflow: false,
      renderCell: ({ row }) => (
        <ApprovalCell
          enforcement={row.enforcement}
          approval={row.approval}
          disabled={controlsDisabled}
          onUndo={() => row.enforcement && onUndo(row, row.enforcement)}
        />
      ),
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [{ value: FilterOperators.INCLUDES, label: 'Includes' }],
      statePersistence: { urlKey: URL_KEYS[FIELDS.APPROVAL] },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [
          { value: 'reviewed', label: 'Reviewed' },
          { value: 'not_reviewed', label: 'Not reviewed' },
        ],
      },
    },
  ];
}
